import L, {
  Bounds,
  LatLngBounds,
  Rectangle,
  latLngBounds,
  Point,
  Polyline,
  LayerGroup,
  Polygon,
  Draw,
  marker,
  latLng
} from "leaflet"
import type { DrawMap, DrawOptions, LatLng, LatLngExpression, LeafletEvent, LeafletMouseEvent, Marker } from "leaflet"
import "leaflet-draw"
import "proj4leaflet"

export function overwriteLeafletProjections(): void {}

export function overwriteCRSDistanceFn(crs: L.Proj.CRS): void {
  crs.distance = (latlng1: LatLngExpression, latlng2: LatLngExpression) => {
    return L.CRS.Earth.distance(latlng1, latlng2)
  }
}

export function overwriteArrowheadFn(): void {
  ;(L.Symbol.ArrowHead.prototype as any)._buildArrowPath = function (dirPoint: any, map: DrawMap) {
    const d2r = Math.PI / 180
    const tipPoint = map.project(dirPoint.latLng)
    const direction = -(dirPoint.heading - 90) * d2r
    const radianArrowAngle = (this.options.headAngle / 2) * d2r

    const headAngle1 = direction + radianArrowAngle
    const headAngle2 = direction - radianArrowAngle
    const arrowHead1 = L.point(
      tipPoint.x - this.options.pixelSize * Math.cos(headAngle1),
      tipPoint.y + this.options.pixelSize * Math.sin(headAngle1)
    )
    const arrowHead2 = L.point(
      tipPoint.x - this.options.pixelSize * Math.cos(headAngle2),
      tipPoint.y + this.options.pixelSize * Math.sin(headAngle2)
    )

    return [map.unproject(arrowHead1), dirPoint.latLng, map.unproject(arrowHead2)]
  }
  ;(L.PolylineDecorator.prototype as any)._getPatternLayers = function (pattern: any) {
    const mapBounds: LatLngBounds = this._map.getBounds()
    const point1: Point = this._map.latLngToLayerPoint(mapBounds.getNorthWest())
    const point2: Point = this._map.latLngToLayerPoint(mapBounds.getSouthEast())
    const bounds: Bounds = new Bounds(point1.subtract([100, 100]), point2.add([100, 100]))
    return this._paths.map((path: any) => {
      const directionPoints = this._getDirectionPoints(path, pattern)
      const filteredPoints = directionPoints.filter((directionPoint: { latLng: LatLng; heading: number }) => {
        const point: Point = this._map.latLngToLayerPoint(directionPoint.latLng)
        return bounds.contains(point)
      })
      return L.featureGroup(this._buildSymbols(path, pattern.symbolFactory, filteredPoints))
    })
  }
}

export function overwritePopupFns(): void {
  ;(L.Popup.prototype as any).update = function () {
    if (!this._map) {
      return
    }

    this._updateContent()
    this._updateLayout()
    this._updatePosition()

    this._adjustPan()
  }
  ;(L.CircleMarker.prototype as any).setLatLng = function (coord: LatLng) {
    this._latlng = latLng(coord)
    this.redraw()
  }
}

export const CrsSvgOverlayOverwrite: (new (imageUrl: string, bounds: Bounds, options: any) => any) & typeof L.Class =
  L.Proj.ImageOverlay.extend({
    _initImage: function () {
      this._image = L.DomUtil.create(
        "svg",
        `custom-svg-layer leaflet-image-layer ${this._zoomAnimated ? "leaflet-zoom-animated" : ""}`
      )

      this._image.onselectstart = L.Util.falseFn
      this._image.onmousemove = L.Util.falseFn
    },

    setId: function (id: string) {
      this._image.id = id
    },

    setInnerHtml: function (svgString: string) {
      this._image.innerHTML = svgString
    }
  })

export const SvgOverlayOverwrite: (new (imageUrl: string, bounds: LatLngBounds, options: any) => any) & typeof L.Class =
  L.ImageOverlay.extend({
    _initImage: function () {
      this._image = L.DomUtil.create(
        "svg",
        `custom-svg-layer leaflet-image-layer ${this._zoomAnimated ? "leaflet-zoom-animated" : ""}`
      )

      this._image.onselectstart = L.Util.falseFn
      this._image.onmousemove = L.Util.falseFn
    },

    setId: function (id: string) {
      this._image.id = id
    },

    setInnerHtml: function (svgString: string) {
      this._image.innerHTML = svgString
    }
  })

export function crsSvgOverlayOverwrite(imageUrl: string, bounds: Bounds, options?: any) {
  return new CrsSvgOverlayOverwrite(imageUrl, bounds, options)
}

export function svgOverlayOverwrite(imageUrl: string, bounds: LatLngBounds, options?: any) {
  return new SvgOverlayOverwrite(imageUrl, bounds, options)
}

export type RectangleOverwrite = (new (
  corner1: LatLng,
  corner2: LatLng,
  options?: L.PolylineOptions
) => RectangleOverwriteLayer) &
  typeof L.Class

export type RectangleOverwriteLayer = Rectangle & {
  _corner1: LatLng
  _corner2: LatLng
}

export const rectangleOverwrite: RectangleOverwrite = L.Rectangle.extend({
  initialize: function (corner1: LatLng, corner2: LatLng, options?: L.PolylineOptions) {
    this._corner1 = corner1
    this._corner2 = corner2

    const nativeBounds: LatLngBounds = new LatLngBounds(corner1, corner2)
    ;(Rectangle.prototype as any).initialize.call(this, this._boundsToLatLngs(nativeBounds), options)
  },
  _boundsToLatLngs: function (bounds: LatLngBounds) {
    // biome-ignore lint/style/noParameterAssign: TODO in a later PR
    bounds = (latLngBounds as any)(bounds)

    const latLngs: LatLng[] = [
      bounds.getSouthWest(),
      bounds.getNorthWest(),
      bounds.getNorthEast(),
      bounds.getSouthEast()
    ]

    const cornerIndex: number = latLngs.findIndex((latLng) => {
      return latLng.lat === this._corner1.lat && latLng.lng === this._corner1.lng
    })

    if (cornerIndex === -1) return latLngs

    const sortedLatLngs: LatLng[] = []
    for (let i = cornerIndex; i < cornerIndex + latLngs.length; i++) {
      sortedLatLngs.push(latLngs[i % latLngs.length])
    }

    return sortedLatLngs
  },
  setBounds: function (corner1: LatLng, corner2: LatLng) {
    this._corner1 = corner1
    this._corner2 = corner2
    const newBounds: LatLngBounds = new LatLngBounds(corner1, corner2)

    return this.setLatLngs(this._boundsToLatLngs(newBounds))
  },
  _projectLatlngs: function (_: LatLng[], result: Point[][], projectedBounds: Bounds) {
    const point1: Point = this._map.latLngToLayerPoint(this._corner1)
    const point3: Point = this._map.latLngToLayerPoint(this._corner2)

    const point2: Point = new Point(point3.x, point1.y)
    const point4: Point = new Point(point1.x, point3.y)

    projectedBounds.extend(point1)
    projectedBounds.extend(point2)
    projectedBounds.extend(point3)
    projectedBounds.extend(point4)

    result.push([point1, point2, point3, point4])
  }
})

export const RectangleDrawOverwrite: (new (
  map: DrawMap,
  options?: DrawOptions.RectangleOptions
) => any) &
  typeof L.Class = L.Draw.Rectangle.extend({
  initialize: function (map: DrawMap, options: DrawOptions.RectangleOptions) {
    L.Draw.Rectangle.prototype.initialize.call(this, map, options)
  },
  _drawShape: function (latlng: LatLng) {
    if (!this._shape) {
      this._shape = new rectangleOverwrite(this._startLatLng, latlng, this.options.shapeOptions)
      this._map.addLayer(this._shape)
    } else {
      this._shape.setBounds(this._startLatLng, latlng)
    }
  },

  _fireCreatedEvent: function () {
    const rectangle = new rectangleOverwrite(this._shape._corner1, this._shape._corner2, this.options.shapeOptions)
    ;(L.Draw.SimpleShape.prototype as any)._fireCreatedEvent.call(this, rectangle)
  }
})

export const RectangleEditOverwrite: (new (
  shape: RectangleOverwriteLayer,
  options?: L.EditOptions.EditSimpleShapeOptions
) => any) &
  typeof L.Class = L.Edit.Rectangle.extend({
  initialize: function (shape: RectangleOverwriteLayer, options: L.EditOptions.EditSimpleShapeOptions) {
    if (L.Browser.touch) {
      this.options.moveIcon = this.options.touchMoveIcon
      this.options.resizeIcon = this.options.touchResizeIcon
    }

    this._shape = shape
    L.Util.setOptions(this, options)
  },
  _getCorners: function () {
    const corners: LatLng[] = this._shape._boundsToLatLngs(this._shape._bounds)
    const point1: Point = this._map.latLngToLayerPoint(corners[0])
    const point3: Point = this._map.latLngToLayerPoint(corners[2])

    const point2: Point = new Point(point3.x, point1.y)
    const point4: Point = new Point(point1.x, point3.y)

    const coord2: LatLng = this._map.layerPointToLatLng(point2)
    const coord4: LatLng = this._map.layerPointToLatLng(point4)

    return [corners[0], coord2, corners[2], coord4]
  },
  _getPoints: function () {
    const corners: LatLng[] = this._shape._boundsToLatLngs(this._shape._bounds)
    const point1: Point = this._map.options.crs.project(corners[0])
    const point3: Point = this._map.options.crs.project(corners[2])

    const point2: Point = new Point(point3.x, point1.y)
    const point4: Point = new Point(point1.x, point3.y)

    return [point1, point2, point3, point4]
  },
  _getCenter: function () {
    const centerPoint: Point = this._getCenterPoint()
    return this._shape._map.layerPointToLatLng(centerPoint)
  },
  _getCenterPoint: function () {
    const { _corner1, _corner2 } = this._shape as RectangleOverwriteLayer

    const point1: Point = this._shape._map.latLngToLayerPoint(_corner1)
    const point3: Point = this._shape._map.latLngToLayerPoint(_corner2)

    const minX: number = Math.min(point1.x, point3.x)
    const maxX: number = Math.max(point1.x, point3.x)
    const minY: number = Math.min(point1.y, point3.y)
    const maxY: number = Math.max(point1.y, point3.y)

    const x: number = (maxX - minX) / 2 + minX
    const y: number = (maxY - minY) / 2 + minY

    return new Point(x, y)
  },
  _createMoveMarker: function () {
    this._moveMarker = this._createMarker(this._getCenter(), this.options.moveIcon)
  },
  _createResizeMarker: function () {
    const latlngs: LatLng[] = this._getCorners()
    this._resizeMarkers = []

    for (let index = 0; index < latlngs.length; index++) {
      this._resizeMarkers.push(this._createMarker(latlngs[index], this.options.resizeIcon))
      this._resizeMarkers[index]._cornerIndex = index
    }
  },
  _move: function (newCenter: LatLng) {
    const centerPoint: Point = this._getCenterPoint()
    let newCenterPoint: Point = this._map.latLngToLayerPoint(newCenter)

    const xOffset: number = newCenterPoint.x - centerPoint.x
    const yOffset: number = newCenterPoint.y - centerPoint.y
    const offsetPoint: Point = new Point(xOffset, yOffset)

    const { _corner1, _corner2 } = this._shape as RectangleOverwriteLayer

    const point1: Point = this._map.latLngToLayerPoint(_corner1)
    const point3: Point = this._map.latLngToLayerPoint(_corner2)

    const newPoint1: Point = point1.add(offsetPoint)
    const newPoint3: Point = point3.add(offsetPoint)

    const minX: number = Math.min(newPoint1.x, newPoint3.x)
    const maxX: number = Math.max(newPoint1.x, newPoint3.x)
    const minY: number = Math.min(newPoint1.y, newPoint3.y)
    const maxY: number = Math.max(newPoint1.y, newPoint3.y)

    const x: number = (maxX - minX) / 2 + minX
    const y: number = (maxY - minY) / 2 + minY

    newCenterPoint = new Point(x, y)
    // biome-ignore lint/style/noParameterAssign: TODO in a later PR
    newCenter = this._map.layerPointToLatLng(newCenterPoint)

    const coord1: LatLng = this._map.layerPointToLatLng(newPoint1)
    const coord3: LatLng = this._map.layerPointToLatLng(newPoint3)

    this._shape.setBounds(coord1, coord3)
    this._repositionCornerMarkers()

    this._map.fire(L.Draw.Event.EDITMOVE, { layer: this._shape })
  },
  _onMarkerDragEnd: function (event: LeafletEvent) {
    const marker: Marker = event.target

    if (marker === this._moveMarker) {
      marker.setLatLng(this._getCenter())
    }

    this._toggleCornerMarkers(1)
    this._repositionCornerMarkers()
    ;(L.Edit.SimpleShape.prototype as any)._onMarkerDragEnd.call(this, event)
  },
  _resize: function (latlng: LatLng) {
    this._shape.setBounds(latlng, this._oppositeCorner)
    this._moveMarker.setLatLng(this._getCenter())
    this._map.fire(L.Draw.Event.EDITRESIZE, { layer: this._shape })
  }
})

rectangleOverwrite.addInitHook(function (this: any) {
  if (RectangleEditOverwrite) {
    this.editing = new RectangleEditOverwrite(this)
  }
})

export type PolylineOverwrite = (new (
  latlngs: L.LatLngExpression[] | L.LatLngExpression[][],
  options?: L.PolylineOptions
) => PolylineOverwriteLayer) &
  typeof L.Class

export type PolylineOverwriteLayer = Polyline

export const polylineOverwrite: PolylineOverwrite = Polyline.extend({
  _test: "test"
})

export const PolylineDrawOverwrite: (new (map: DrawMap, options: DrawOptions.PolygonOptions) => any) & L.Class =
  Draw.Polyline.extend({
    Poly: polylineOverwrite,
    addHooks: function () {
      ;(Draw.Feature.prototype as any).addHooks.call(this)
      if (this._map) {
        this._markers = []

        this._markerGroup = new LayerGroup()
        this._map.addLayer(this._markerGroup)

        this._poly = new polylineOverwrite([], this.options.shapeOptions)

        this._tooltip.updateContent(this._getTooltipText())

        if (!this._mouseMarker) {
          this._mouseMarker = marker(this._map.getCenter(), {
            icon: L.divIcon({
              className: "leaflet-mouse-marker",
              iconAnchor: [20, 20],
              iconSize: [40, 40]
            }),
            opacity: 0,
            zIndexOffset: this.options.zIndexOffset
          })
        }

        this._mouseMarker
          .on("mouseout", this._onMouseOut, this)
          .on("mousemove", this._onMouseMove, this)
          .on("mousedown", this._onMouseDown, this)
          .on("mouseup", this._onMouseUp, this)
          .addTo(this._map)

        this._map
          .on("mouseup", this._onMouseUp, this)
          .on("mousemove", this._onMouseMove, this)
          .on("zoomlevelschange", this._onZoomEnd, this)
          .on("touchstart", this._onTouch, this)
          .on("zoomend", this._onZoomEnd, this)
      }
    },
    _fireCreatedEvent: function () {
      const poly: PolylineOverwriteLayer = new this.Poly(this._poly.getLatLngs(), this.options.shapeOptions)
      ;(L.Draw.Feature.prototype as any)._fireCreatedEvent.call(this, poly)
    }
  })

export const PolylineEditOverwrite: (new (
  shape: PolylineOverwriteLayer,
  options?: L.EditOptions.EditPolyVerticesEditOptions
) => any) &
  typeof L.Class = L.Edit.PolyVerticesEdit.extend({
  _testEdit: "testEdit",
  initialize: function (poly: PolylineOverwriteLayer) {
    ;(L.Edit.PolyVerticesEdit.prototype as any).initialize.call(this, poly)
    this._latlngs = (poly as any)._latlngs
    if ((poly as any)._holes) {
      this._latlngs = this.latlngs.concat((poly as any)._holes)
    }

    this._poly = poly

    this._poly.on("revert-edited", this._updateLatLngs, this)
  },
  _createMiddleMarker: function (marker1: Marker, marker2: Marker) {
    const latlng: LatLng = this._getMiddleLatLng(marker1, marker2)
    const marker: Marker = this._createMarker(latlng)
    ;(marker1 as any)._middleRight = marker
    ;(marker2 as any)._middleLeft = marker

    const onDrag: (event: LeafletMouseEvent) => void = function (this: any, event: LeafletMouseEvent): void {
      const oldLatLng: LatLng = (marker as any)._oldLatLng ? (marker as any)._oldLatLng : (event as any).oldLatLng
      const oldPoint: Point = this._map.latLngToLayerPoint(oldLatLng)
      const point: Point = this._map.latLngToLayerPoint(event.latlng)

      const offsetX: number = point.x - oldPoint.x
      const offsetY: number = point.y - oldPoint.y

      const offsetPoint: Point = new Point(offsetX, offsetY)
      const coords: LatLng[] = this._latlngs

      const newCoords = coords.map((coord: LatLng) => {
        return this._map.layerPointToLatLng(this._map.latLngToLayerPoint(coord).add(offsetPoint))
      })

      this._latlngs = newCoords
      this._poly.setLatLngs(newCoords)
      this._markers.forEach((m: Marker, index: number) => {
        m.setLatLng(newCoords[index])
      })
      ;(marker as any)._oldLatLng = event.latlng
      this._map.fire(L.Draw.Event.EDITMOVE, { layer: this._poly })
    }

    const onDragEnd: () => void = function (this: any): void {
      this.updateMarkers()
    }
    ;(marker as any).on("drag", onDrag, this).on("dragend", onDragEnd, this)

    this._markerGroup.addLayer(marker)
  },
  _defaultShape: function () {
    const isFlat: (latlngs: LatLng[] | LatLng[][]) => boolean | undefined = (Polyline as any).isFlat
    const latlngs: LatLng[] | LatLng[][] = this._poly._latlngs
    return isFlat ? (isFlat(latlngs) ? latlngs : latlngs[0]) : latlngs
  },
  updateMarker: function () {
    this._markerGroup.clearLayers()
    this._initMarkers()
  },
  _initMarkers: function () {
    if (!this._markerGroup) {
      this._markerGroup = new LayerGroup()
    }
    this._markers = []

    const latlngs: LatLng[] = this._defaultShape()
    let i: number
    let j: number
    let len: number
    let marker: Marker

    for (i = 0, len = latlngs.length; i < len; i++) {
      marker = this._createMarker(latlngs[i], i)
      marker.on("click", this._onMarkerClick, this)
      marker.on("contextmenu", this._onContextMenu, this)
      marker.on(
        "drag",
        function (this: any) {
          this._poly._map.fire(L.Draw.Event.EDITRESIZE, {
            layer: this._poly
          })
        },
        this
      )
      this._markers.push(marker)
    }

    let markerLeft: Marker
    let markerRight: Marker

    for (i = 0, j = len - 1; i < len; j = i++) {
      if (i === 0 && !(Polygon && this._poly instanceof Polygon)) {
        continue
      }

      markerLeft = this._markers[j]
      markerRight = this._markers[i]

      if (this._middleMarker) {
        this._markerGroup.addLayer(this._middleMarker)
        ;(markerLeft as any)._middleRight = this._middleMarker
        ;(markerRight as any)._middleLeft = this._middleMarker
      } else {
        this._createMiddleMarker(markerLeft, markerRight)
        this._updatePrevNext(markerLeft, markerRight)
      }
    }
  }
})

polylineOverwrite.addInitHook(function (this: any) {
  if (this.editing && this.editing instanceof PolylineEditOverwrite) {
    return
  }

  if (PolylineEditOverwrite) {
    this.editing = new PolylineEditOverwrite(this)

    if (this.options.editable) {
      this.editing.enable()
    }
  }

  this.on("add", function (this: any) {
    if (this.editing?.enabled()) {
      this.editing.addHooks()
    }
  })
})
